import { useCallback, useRef } from "react";
import "./RoleManagement.styles.scss";

// Component Imports
import { AGTable } from "../../components";
import AddUserModal from "../../components/Modals/RoleManagement/AddUserModal/AddUserModal";
import UserDetailsModal from "../../components/Modals/RoleManagement/UserDetailsModal/UserDetailsModal";
import DeleteModal from "../../components/Modals/DeleteModal/DeleteModal";

// Context Imports
import { useAppContext } from "../../context/context";

// Material UI Imports
import { Alert, Button, Snackbar, SvgIcon } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";

// Hook Imports
import { useRoleManagement } from "./hooks/useRoleManagement";
import { useRoleManagementTable } from "./hooks/useRoleManagementTable";
import { useAddUserModal } from "./hooks/useAddUserModal";
import { useUserDetailsModal } from "./hooks/useUserDetailsModal";
import { useDeleteUserModal } from "./hooks/useDeleteUserModal";
import { useAlerts } from "@/hooks";
import { getErrorMessage } from "@/utils/getErrorMessage";
import {
  useUpdateOrgMemberRolesMutation,
  useUpdateOrgMemberPortfoliosMutation,
} from "./services/roleManagementApi";

// Util Imports
import { formatIsoDateToLocale } from "@pulse-ui/formatting";

// Layout Imports
import { Loading } from "../../layout";

// SVG Imports
import LogoSVG from "@/svgs/pulse_prime_logo.svg?react";

const RoleManagement = () => {
  const gridRef = useRef<any>();
  const { context_state } = useAppContext();
  const { showError } = useAlerts();

  // Data
  const { orgData, dataMap, loading } = useRoleManagement();

  // Mutations
  const [updateUserRoles] = useUpdateOrgMemberRolesMutation();
  const [updateUserPortfolios] = useUpdateOrgMemberPortfoliosMutation();

  // Modal hooks
  const addUserModal = useAddUserModal(orgData?.org_id);
  const userDetailsModal = useUserDetailsModal();
  const deleteModal = useDeleteUserModal(orgData?.org_id);

  // Role change handler — also clears portfolios if all roles removed
  const handleRoleChange = useCallback(
    async (orgUserId: string, roles: string[]) => {
      await updateUserRoles({ orgUserId, roles }).unwrap();
      userDetailsModal.syncRoles(orgUserId, roles);
      if (roles.length === 0) {
        await updateUserPortfolios({ orgUserId, portfolios: [] }).unwrap();
        userDetailsModal.syncPortfolios(orgUserId, []);
      }
    },
    [updateUserRoles, updateUserPortfolios, userDetailsModal.syncRoles, userDetailsModal.syncPortfolios],
  );

  // Portfolio update handler
  const handleUpdateUserPortfolios = useCallback(
    async (orgUserId: string, portfolios: string[]) => {
      await updateUserPortfolios({ orgUserId, portfolios }).unwrap();
      userDetailsModal.syncPortfolios(orgUserId, portfolios);
    },
    [updateUserPortfolios, userDetailsModal.syncPortfolios],
  );

  // Delete modal submit — differentiates between user and portfolio deletion
  const handleDeleteSubmit = useCallback(async () => {
    const { delete_type, item_id, org_user_id } = deleteModal.deleteModalStatus;

    if (delete_type === "user") {
      await deleteModal.handleDeleteUser();
    } else if (delete_type === "portfolio" && item_id && org_user_id) {
      try {
        const currentPortfolios = userDetailsModal.userDetails.portfolios ?? [];
        const updatedPortfolios = currentPortfolios.filter(
          (p: string) => p !== item_id,
        );
        await updateUserPortfolios({
          orgUserId: org_user_id,
          portfolios: updatedPortfolios,
        }).unwrap();
        userDetailsModal.syncPortfolios(org_user_id, updatedPortfolios);
        deleteModal.closeModal();
      } catch (err) {
        showError(`Failed to remove portfolio: ${getErrorMessage(err)}`);
        deleteModal.closeModal();
      }
    }
  }, [
    deleteModal,
    userDetailsModal.userDetails,
    updateUserPortfolios,
    showError,
  ]);

  // Table config
  const { colDefs, defaultColDef, getContextMenuItems } =
    useRoleManagementTable({
      onViewDetails: userDetailsModal.openModal,
      onDeleteUser: deleteModal.openDeleteUser,
    });

  const rowData = Array.from(dataMap.values());
  const hasUsers = rowData.length > 0;

  return (
    <div className="pulse-sidenav-adjusted-container pulse-bg-color--background-900">
      <SvgIcon
        sx={{
          width: "100%",
          height: "100%",
          position: "absolute",
          top: 0,
          left: 0,
        }}
        children={<LogoSVG className="pulse-color--background-800" />}
      />

      <p className="pulse-typography-headline-6 pulse-color--background-50 pulse-row pulse-mb-1">
        Role Management
      </p>

      <div
        className="
                pulse-row
                role-management-header-row
                role-management-position-relative
                pulse-col-md-12
                pulse-col-lg-12
                pulse-col-xl-12
                pulse-p-1
                pulse-bg-color--background-800
                pulse-mb-3
            "
      >
        <div className="role-management-header-section-1 pulse-mb-2">
          <p className="pulse-typography-headline-5 pulse-color--background-50">
            {orgData?.display_name
              ? `Organization: ${orgData.display_name}`
              : "Organization"}
          </p>

          {orgData && (
            <div className="role-management-active-status pulse-typography-headline-6">
              {orgData.is_active ? (
                <>
                  <CheckCircleIcon className="role-management-active-status-active-icon pulse-mr-1" />
                  <p className="pulse-color--success-700">Active</p>
                </>
              ) : (
                <>
                  <ErrorIcon className="role-management-active-status-error-icon pulse-mr-1" />
                  <p className="pulse-color--error-700">Not Active</p>
                </>
              )}
            </div>
          )}
        </div>

        <div className="role-management-header-section-2">
          {orgData && (
            <p className="pulse-typography-subtitle-1 pulse-color--background-100 pulse-mb-1">
              {`Created: ${formatIsoDateToLocale(orgData.created)}`}
            </p>
          )}
          {orgData && (
            <p className="pulse-typography-subtitle-1 pulse-color--background-100">
              {`Last Modified: ${formatIsoDateToLocale(orgData.last_modified)}`}
            </p>
          )}
        </div>
      </div>

      <hr
        className="role-management-position-relative pulse-mb-2"
        style={{ borderColor: "#627195" }}
      />

      <div className="pulse-row pulse-mt-1 pulse-mb-1 role-management-subheader-row">
        <div className="pulse-col-sm-6 pulse-col-md-6 pulse-col-lg-6 pulse-col-xl-6 pulse-justify-flex-start">
          <p className="pulse-typography-headline-6 pulse-color--background-50">
            {hasUsers ? "Users" : "No Users in Organization"}
          </p>
        </div>
        <div className="pulse-col-sm-6 pulse-col-md-6 pulse-col-lg-6 pulse-col-xl-6 pulse-justify-flex-end">
          <Button
            type="submit"
            variant="contained"
            startIcon={<AddIcon />}
            onClick={addUserModal.openModal}
            sx={{
              backgroundColor: "success.700",
              color: "success.100",
              typography: "fontSizeSmall",
              "&:hover": { backgroundColor: "success.600" },
              "&:disabled": {
                backgroundColor: "success.800",
                color: "success.300",
              },
            }}
          >
            Add New User
          </Button>
        </div>
      </div>

      <AGTable
        gridRef={gridRef}
        rowData={rowData}
        colDefs={colDefs}
        defaultColDef={defaultColDef}
        groupDisplayType="singleColumn"
        sideBar={false}
        animateRows
        getContextMenuItems={getContextMenuItems}
      />

      <div className="role-management-position-relative">
        {addUserModal.open && (
          <AddUserModal
            open
            addUserForm={addUserModal.addUserForm}
            disableSaveButton={addUserModal.disableSaveButton}
            handleAddUserFormChange={addUserModal.handleAddUserFormChange}
            closeAddUserModal={addUserModal.closeAddUserModal}
            handlePostAddUser={addUserModal.handlePostAddUser}
          />
        )}

        {userDetailsModal.open && (
          <UserDetailsModal
            open
            userDetails={userDetailsModal.userDetails}
            portfolios_context_data={
              context_state.org_metadata?.portfolios ?? []
            }
            closeUserDetailsModal={userDetailsModal.closeUserDetailsModal}
            copyToClipboard={userDetailsModal.copyToClipboard}
            setDeleteModalStatus={deleteModal.setDeleteModalStatus}
            handleRoleChange={handleRoleChange}
            handleUpdateUserPortfolios={handleUpdateUserPortfolios}
          />
        )}

        {deleteModal.deleteModalStatus.open && (
          <DeleteModal
            open
            item_name={deleteModal.deleteModalStatus.item_name}
            handleClose={deleteModal.closeModal}
            handleSubmit={handleDeleteSubmit}
          />
        )}
      </div>

      <Snackbar
        open={userDetailsModal.clipboardSnackbar.open}
        autoHideDuration={5000}
        onClose={userDetailsModal.handleCloseSnackbar}
        resumeHideDuration={0}
      >
        <Alert
          onClose={userDetailsModal.handleCloseSnackbar}
          severity={
            userDetailsModal.clipboardSnackbar.success ? "success" : "error"
          }
          variant="filled"
          sx={{
            width: "100%",
            backgroundColor: userDetailsModal.clipboardSnackbar.success
              ? "#0d732a"
              : "#8c060d",
          }}
        >
          {userDetailsModal.clipboardSnackbar.message}
        </Alert>
      </Snackbar>

      {loading && <Loading />}
    </div>
  );
};

export default RoleManagement;
